import { getSubscriptionLabel, PriceLabel } from '@/components/features/shared/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SelectableRow } from '@/components/ui/selectable-row'
import { installmentsOptions } from '@/constants/installmentOptions'
import { BillingPeriodStatus, SubscriptionData } from '@/domain/subscriptions/subscriptions'
import { AlertCircle, CalculatorIcon, CheckSquare, X } from 'lucide-react'
import { useEffect, useState } from 'react'

interface CustomBillingPeriodsModalProps {
  isOpen: boolean
  onClose: () => void
  subscriptions: SubscriptionData[]
  onCreateCustomPayment: (subscriptionId: string, billingPeriodIds: string[], installments: number, installmentAmount: number) => void
  isPending: boolean
  onSuccess?: () => void
}

export const CustomBillingPeriodsModal = ({
  isOpen,
  onClose,
  subscriptions,
  onCreateCustomPayment,
  isPending,
  onSuccess,
}: CustomBillingPeriodsModalProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [installments, setInstallments] = useState<number>(2)
  const [installmentAmount, setInstallmentAmount] = useState<number>(0)

  const pendingPeriods = subscriptions.filter(s => {
    if (s.periodStatus === BillingPeriodStatus.CANCELLED) return false
    if (s.periodStatus === BillingPeriodStatus.PAID) return false
    // if (s.outstandingAmount <= 0) return false
    // if (s.outstandingAmount !== s.totalAmount) return false
    return true
  })

  const totalAmount = pendingPeriods
    .filter(s => selectedIds.has(s.billingPeriodId))
    .reduce((sum, s) => sum + s.outstandingAmount, 0)

  useEffect(() => {
    setInstallmentAmount(totalAmount)
  }, [totalAmount])

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const selectAll = () => {
    setSelectedIds(prev => {
      if (prev.size === pendingPeriods.length) {
        return new Set()
      }
      return new Set(pendingPeriods.map(s => s.billingPeriodId))
    })
  }

  const handleConfirm = () => {
    if (selectedIds.size === 0) return
    if (installments <= 0 || installmentAmount <= 0) return
    onCreateCustomPayment(
      subscriptions.find(s => selectedIds.has(s.billingPeriodId))?.subscriptionId || '',
      Array.from(selectedIds),
      installments,
      installmentAmount
    )
    onClose()
    setSelectedIds(new Set())
    onSuccess?.()
  }

  if (pendingPeriods.length === 0) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generar cuotas de períodos</DialogTitle>
            <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
              <AlertCircle className="h-4 w-4" />
              No hay períodos válidos (pendientes y sin pagos parciales) para seleccionar
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
            </DialogFooter>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Crear plan de pagos para múltiples períodos</DialogTitle>
          <DialogDescription>
            Selecciona los períodos pendientes para crear un plan de pagos personalizado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-4 flex-grow flex flex-col min-h-0">
          <div className="flex items-center justify-between pb-2 border-b shrink-0">
            <span className="text-sm text-muted-foreground font-medium">
              {pendingPeriods.length} período{pendingPeriods.length !== 1 ? 's' : ''} válido{pendingPeriods.length !== 1 ? 's' : ''}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={selectAll}
              disabled={isPending}
            >
              <CheckSquare className="h-3 w-3 mr-1" />
              Seleccionar todos
            </Button>
          </div>

          <div className="space-y-2 overflow-y-auto pr-2 flex-grow">
            {pendingPeriods.map(sub => {
              const isSelected = selectedIds.has(sub.billingPeriodId)
              return (
                <SelectableRow
                  key={sub.billingPeriodId}
                  checked={isSelected}
                  disabled={isPending}
                  onCheckChange={() => toggleSelection(sub.billingPeriodId)}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={isSelected}
                      disabled={isPending}
                    />
                    <div>
                      <span className="mt-1 inline-block text-md">
                        <span className='font-medium'>Período </span>
                        <span className='font-medium font-mono'>{getSubscriptionLabel(sub.periodStartDate, sub.periodEndDate, sub.billingFrequency)}</span>
                      </span>
                      <div className="text-sm ">
                        {new Date(sub.periodStartDate).toLocaleDateString('es-AR')} —{' '}
                        {new Date(sub.periodEndDate).toLocaleDateString('es-AR')}
                      </div>
                    </div>
                  </div>
                  <PriceLabel price={sub.outstandingAmount} />
                </SelectableRow>
              )
            })}
          </div>
        </div>

        <Card className="my-4">
          <CardContent className="p-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-muted/20 shrink-0">
              <div className="space-y-2">
                <Label>Monto Total a financiar</Label>
                <Input
                  type="number"
                  value={installmentAmount || ''}
                  onChange={e => setInstallmentAmount(Number(e.target.value))}
                  disabled={isPending}
                  min={1}
                />
              </div>
              <div className="space-y-2">
                <Label>Cantidad de Cuotas</Label>
                <Select
                  value={installments.toString()}
                  onValueChange={v => setInstallments(Number(v))}
                  disabled={isPending}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar cuotas" />
                  </SelectTrigger>
                  <SelectContent>
                    {installmentsOptions.map(opt => (
                      <SelectItem key={opt} value={opt.toString()}>
                        {opt} cuotas
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 pt-2 text-sm text-muted-foreground flex justify-between border-t mt-2">
                <span>Monto final por cuota:</span>
                <span className="font-bold text-foreground">
                  ${(installmentAmount / installments || 0).toLocaleString('es-AR', { maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between bg-muted/30 p-3 rounded-lg border">
          <span className="text-sm font-medium">Deuda original de periodos seleccionados:</span>
          <span className="text-lg font-bold font-mono">${totalAmount.toLocaleString('es-AR')}</span>
        </div>

        <DialogFooter className="mt-4">
          <div className='flex justify-between w-full'>
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              <X className="h-3.5 w-3.5 mr-1.5" />
              Cancelar
            </Button>
            <Button
              variant="info"
              onClick={handleConfirm}
              disabled={selectedIds.size === 0 || isPending || installmentAmount <= 0}
            >
              <CalculatorIcon className="h-3.5 w-3.5 mr-1" />
              Crear plan de pago
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
